import { z } from 'zod/v4'
import type { Field, LabelType } from '@skop-app/lib-domain/types'

import { AddProducts } from '@/components/organisms/AddProducts/addProducts.organism'
import type {
  OrderModalValues,
  SignDocumentTabOptions,
  Translator,
} from '@/types'
import { OrderPriceIndicator } from '@/components/atoms/OrderPriceIndicator/orderPriceIndicator.atom'
import {
  localeDecimalStringValidation,
  numberStringValidation,
} from './schemaValidations'
import { UserPlusIcon } from '@phosphor-icons/react'
import { openModal } from '@/components/modals/modal.registry.events'

type CustomerOption = LabelType & {
  email?: string
  phone?: string
  address?: string
  city?: string
  zipcode?: string
  country?: string
}

const createBonusFieldChangeHandler = (
  fieldName:
    | 'orderBonusFieldOne'
    | 'orderBonusFieldTwo'
    | 'orderBonusFieldThree',
  setOrderValues: React.Dispatch<React.SetStateAction<OrderModalValues>>,
) => {
  let animationId: number | null = null

  return (e: React.ChangeEvent<HTMLInputElement>) => {
    if (animationId) {
      cancelAnimationFrame(animationId)
    }

    const value = e.target.value

    animationId = requestAnimationFrame(() => {
      setOrderValues((prev: OrderModalValues) => ({
        ...prev,
        [fieldName]: value,
      }))
    })
  }
}

export const getQuoteFields = ({
  t,
  currency,
  locale,
  isLoading,
  options,
  orderValues,
  setOrderValues,
  addProducts,
  customerOptions,
}: {
  t: Translator
  locale: string
  setOrderValues: React.Dispatch<React.SetStateAction<OrderModalValues>>
  currency?: string
  isLoading?: boolean
  options?: SignDocumentTabOptions
  orderValues?: OrderModalValues
  addProducts?: boolean
  customerOptions?: CustomerOption[]
}) => {
  const isQuote =
    orderValues?.requestType === 'quote' ||
    orderValues?.requestType === 'quoteOrder'

  return [
    ...(isQuote
      ? [
          {
            name: 'orderBonusFieldOne',
            type: 'text',
            placeholder: t('Order.quoteTitlePlaceholder'),
            validation: z.string().nullish(),
            onChange: createBonusFieldChangeHandler(
              'orderBonusFieldOne',
              setOrderValues,
            ),
            className: 'col-span-12 md:col-span-6',
          },
          {
            name: 'customer',
            type: 'select',
            placeholder: t('Customers-directory.add-customer'),
            validation: z
              .object({
                label: z.string(),
                value: z.string(),
                email: z.string().optional(),
                phone: z.string().optional(),
                address: z.string().optional(),
                city: z.string().optional(),
                zipcode: z.string().optional(),
                country: z.string().optional(),
              })
              .passthrough()
              .optional()
              .nullable(),
            options: [
              ...(customerOptions ?? []),
              {
                label: t('Customers-directory.add-customer'),
                value: 'addCustomer',
                type: 'custom',
                onClick: () => {
                  openModal('customerUpload', {
                    customerID: undefined,
                  })
                  return false
                },
                icon: <UserPlusIcon className="size-4" />,
                className: 'hover:!bg-primary-300',
              },
            ],
            disabled: options?.hideClient || isLoading,
            overflow: true,
            className: 'col-span-12 md:col-span-6',
          },
          {
            name: 'orderBonusFieldTwo',
            type: 'textarea',
            rows: 2,
            placeholder: t('Order.quoteDescriptionPlaceholder'),
            validation: z.string().nullish(),
            onChange: createBonusFieldChangeHandler(
              'orderBonusFieldTwo',
              setOrderValues,
            ),
            className: 'col-span-12 md:col-span-6',
          },
        ]
      : []),
    {
      name: 'items',
      type: 'fields-array',
      validation: z.array(
        z.looseObject({
          id: z.string().optional(),
          name: z.string(),
          dimensions: z.string().optional(),
          quantity: z.number(),
          unitLabel: z.string(),
          rawPrice: numberStringValidation,
          rawTotal: localeDecimalStringValidation(locale),
        }),
      ),
      hideAddBtn: true,
      hideRemoveBtn: options?.hideAddProducts,
      ...(!orderValues?.items || orderValues.items.length === 0
        ? {
            label: (
              <p className="mt-2 text-center text-sm font-medium sm:text-left">
                {t('Order.selectProductsToFillQuote')}
              </p>
            ),
          }
        : {}),
      fields: [
        {
          name: `name`,
          label: t('Product.designation'),
          type: 'text',
          disabled: true,
          className: 'col-span-12 lg:col-span-3',
          'aria-required': true,
        },
        {
          name: `dimensions`,
          label: t('Product.dimensions'),
          type: 'text',
          disabled: true,
          className: 'col-span-12 lg:col-span-2 text-sm text-zinc-500',
          'aria-required': true,
        },
        {
          name: `quantity`,
          label: t('Product.quantity'),
          type: 'number',
          className: 'col-span-6 lg:col-span-1 text-center',
          disabled: options?.hideAddProducts || isLoading,
          min: 0,
        },
        {
          name: `maxQuantity`,
          type: 'string',
          className:
            'col-span-6 lg:col-span-1 text-center flex pb-2.5 items-end justify-start',
          disabled: true,
        },
        {
          name: `unit`,
          type: 'text',
          className: 'hidden',
        },
        {
          name: `unitLabel`,
          label: t('Product.unit'),
          disabled: true,
          type: 'text',
          inputTextPosition: 'center',
          className: 'col-span-6 lg:col-span-1 text-center',
          'aria-required': true,
        },
        {
          name: `rawPrice`,
          label: t('Product.unit-price') + ` (${currency})`,
          type: 'number',
          inputTextPosition: 'right',
          className: 'col-span-6 lg:col-span-2 text-center line-clamp-1',
          disabled: options?.hideAddProducts || isLoading,
          min: 0,
          'aria-required': true,
        },
        {
          name: `rawTotal`,
          label: t('Product.total-price') + ` (${currency})`,
          type: 'text',
          inputTextPosition: 'right',
          disabled: true,
          className:
            'col-span-5 lg:col-span-2 text-center lg:text-right line-clamp-1',
          'aria-required': true,
        },
      ],
    },
    ...(addProducts
      ? [
          {
            name: 'addProducts',
            type: 'string',
            label: (
              <AddProducts
                orderValues={orderValues || ({} as OrderModalValues)}
                setOrderValues={setOrderValues}
              />
            ),
            validation: z.optional(z.string().nullable().or(z.literal(''))),
            className: 'col-span-12 lg:col-span-6',
          },
        ]
      : []),
    {
      name: 'showTotal',
      type: 'string',
      label: (
        <OrderPriceIndicator
          hideTotal={options?.hideTotal}
          isOrder={
            orderValues?.type !== 'FAVORITE' && !options?.isTransferVoucher
          }
          orderValues={orderValues || ({} as OrderModalValues)}
        />
      ),
      validation: z.optional(z.string().nullable().or(z.literal(''))),
      className: 'col-span-12 lg:col-span-6',
    },
    ...(isQuote
      ? [
          {
            name: 'orderBonusFieldThree',
            type: 'textarea',
            placeholder: t('Order.quoteSpecialMentionsPlaceholder'),
            validation: z.string().nullish(),
            rows: 3,
            onChange: createBonusFieldChangeHandler(
              'orderBonusFieldThree',
              setOrderValues,
            ),
            className: 'col-span-12',
          },
        ]
      : []),
  ] as Field[]
}
